import { useWSQuery, WSQueryConfig } from "@ws-react-query";
import { payersApi } from "@api";
import { WSServiceError } from "../../utils/serviceHelper";

export const QUERY_PAYER_QUICKBOOKS_MAPPING = "QUERY_PAYER_QUICKBOOKS_MAPPING";

export type PayerQuickbooksMapping = {
  quickbooksCustomerId?: string;
  quickbooksItemId?: string;
};

/**
 * A client's payer-wide QuickBooks mapping, read off the RAW `getPayer` body.
 *
 * `GET /v3/payments/payers/:payerId` returns both `quickbooksCustomerId` and
 * `quickbooksItemId` (verified against integration), but the OpenAPI `Payer`
 * read schema declares only the customer id, so the SDK's generated parser —
 * which builds an explicit object rather than spreading the payload — silently
 * drops the item id. `getPayerRaw` keeps the untyped body, the same escape hatch
 * `getVaultFileContentRaw` uses where the typed reader can't express the
 * response.
 *
 * Deliberately NOT sourced from `PayerRow`, which declares both: the row is a
 * materialized search projection, so it lags the write that just changed the
 * mapping and would leave the panel showing the previous value. Delete this hook
 * once the read schema declares `quickbooksItemId` and read it off
 * `queriesPayers.getPayer` instead.
 *
 * Invalidated by `updatePayer` — see the `dependencies` on
 * [[queriesPayers]].
 */
export const useQueryPayerQuickbooksMapping = (
  payerId: string,
  config?: WSQueryConfig<PayerQuickbooksMapping, WSServiceError>
) =>
  useWSQuery<PayerQuickbooksMapping, WSServiceError>(
    [QUERY_PAYER_QUICKBOOKS_MAPPING, payerId],
    async () => {
      const response = await payersApi.getPayerRaw({ payerId });
      const payer = (await response.raw.json()) as PayerQuickbooksMapping;

      return {
        quickbooksCustomerId: payer.quickbooksCustomerId ?? undefined,
        quickbooksItemId: payer.quickbooksItemId ?? undefined
      };
    },
    config
  );
